import { create } from 'zustand'

import { api, request } from '@/api/client'
import type { AddTrackResponse, LibraryItem, SearchResult, SimilarResponse } from '@/api/types'
import { isIncognito, useIncognito } from '@/lib/incognito'

import { recordingKeys } from './recordingKeys'
import { registerContinuation } from './continuation'
import { toQueueItem, usePlayer, type QueueItem } from './store'

export interface Suggestion {
  key: string
  track: SearchResult
  ready: QueueItem | null
  preparing: boolean
}

const PREFERENCE = 'tunes.autoplay'
function readEnabled() {
  try { return localStorage.getItem(PREFERENCE) !== 'off' } catch { return true }
}

let actions: { dismiss: (key: string) => (() => void) | undefined; play: (key: string) => void; retry: () => void } | null = null

export const useAutoplay = create<{
  enabled: boolean
  suggestions: Suggestion[]
  loading: boolean
  message: string | null
  seedName: string
  setEnabled: (enabled: boolean) => void
  dismiss: (key: string) => (() => void) | undefined
  play: (key: string) => void
  retry: () => void
}>((set) => ({
  enabled: readEnabled(), suggestions: [], loading: false, message: null, seedName: '',
  setEnabled: (enabled) => {
    try { localStorage.setItem(PREFERENCE, enabled ? 'on' : 'off') } catch { /* optional preference */ }
    set({ enabled })
  },
  dismiss: (key) => actions?.dismiss(key),
  play: (key) => actions?.play(key),
  retry: () => actions?.retry(),
}))

const resultKeys = (track: SearchResult) => [
  `${track.provider}:${track.external_id}`,
  ...(track.track_id ? [`id:${track.track_id}`] : []),
  ...recordingKeys(track),
]

function delay(signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const aborted = () => { clearTimeout(timer); reject(new Error('Cancelled')) }
    const timer = setTimeout(() => { signal.removeEventListener('abort', aborted); resolve() }, 2000)
    signal.addEventListener('abort', aborted, { once: true })
    if (signal.aborted) aborted()
  })
}

/** Uses the temporary playback endpoint; never saves a recommendation. */
export async function prepareSuggestion(track: Pick<SearchResult, 'provider' | 'external_id'> & Partial<Pick<SearchResult, 'library_item_id' | 'library_status'>>, signal: AbortSignal): Promise<QueueItem> {
  if (track.library_item_id && track.library_status === 'ready') {
    const item = await api.get<LibraryItem>(`/library/tracks/${track.library_item_id}`, undefined, signal)
    if (signal.aborted) throw new Error('Cancelled')
    const ready = item.status === 'ready' ? toQueueItem(item) : null
    if (!ready) throw new Error('Library file unavailable')
    return { ...ready, suggested: true }
  }
  const response = await request<AddTrackResponse>('/playback/tracks', {
    method: 'POST', signal,
    body: { provider: track.provider, external_id: track.external_id, incognito: isIncognito() },
  })
  let item = response.library_item
  for (let attempt = 0; attempt < 90; attempt += 1) {
    if (signal.aborted) throw new Error('Cancelled')
    const ready = item.status === 'ready' ? toQueueItem(item) : null
    if (ready) return { ...ready, suggested: true }
    if (item.status === 'failed' || item.status === 'missing') throw new Error('Track unavailable')
    await delay(signal)
    item = await api.get<LibraryItem>(`/library/tracks/${item.id}`, undefined, signal)
  }
  throw new Error('Preparation timed out')
}

/** One coordinator per authenticated session, independent of whether the queue is open. */
export function startAutoplay() {
  let disposed = false
  let session = -1
  let currentUid = ''
  let excluded = new Set<string>()
  let pool: SearchResult[] = []
  let fetchAbort: AbortController | null = null
  let prepareAbort: AbortController | null = null
  let preparingKey: string | null = null
  let lastFetch = ''
  let failures = 0
  let scheduled = false
  let generation = 0
  let waiting: { intent: number; uid: string; selected?: string } | null = null

  function schedule() {
    if (scheduled || disposed) return
    scheduled = true
    queueMicrotask(() => { scheduled = false; if (!disposed) pump() })
  }

  function stopWaiting() {
    waiting = null
    if (usePlayer.getState().awaitingContinuation) usePlayer.setState({ awaitingContinuation: false })
  }

  function cancelPreparation() {
    prepareAbort?.abort()
    prepareAbort = null
    preparingKey = null
  }

  function reset() {
    generation += 1
    fetchAbort?.abort()
    fetchAbort = null
    cancelPreparation()
    stopWaiting()
    pool = []
    lastFetch = ''
    failures = 0
    useAutoplay.setState({ suggestions: [], loading: false, message: null, seedName: '' })
  }

  function rejectSuggestion(key: string) {
    const suggestion = useAutoplay.getState().suggestions.find((item) => item.key === key)
    const sourceSession = session
    const sourceUid = currentUid
    const sourceGeneration = generation
    const sourcePrivacy = isIncognito()
    const position = useAutoplay.getState().suggestions.findIndex((item) => item.key === key)
    if (suggestion) resultKeys(suggestion.track).forEach((key) => excluded.add(key))
    if (preparingKey === key) cancelPreparation()
    if (waiting?.selected === key) stopWaiting()
    useAutoplay.setState((state) => ({ suggestions: state.suggestions.filter((item) => item.key !== key) }))
    schedule()
    if (!suggestion) return
    return () => {
      if (disposed || generation !== sourceGeneration || isIncognito() !== sourcePrivacy || usePlayer.getState().session !== sourceSession || usePlayer.getState().current()?.uid !== sourceUid || !useAutoplay.getState().enabled) return
      resultKeys(suggestion.track).forEach((identity) => excluded.delete(identity))
      useAutoplay.setState((state) => {
        const suggestions = state.suggestions.filter((item) => item.key !== key)
        suggestions.splice(Math.min(position, suggestions.length), 0, { ...suggestion, preparing: false })
        return { suggestions: suggestions.slice(0, 5) }
      })
      schedule()
    }
  }

  async function fetchSuggestions(current: QueueItem) {
    const controller = new AbortController()
    fetchAbort = controller
    lastFetch = current.uid
    useAutoplay.setState({ loading: true, message: null, seedName: current.artistName })
    try {
      const response = await api.get<SimilarResponse>(current.localArtistId ? `/discover/local/${encodeURIComponent(current.localArtistId)}` : '/discover/similar', {
        artist_id: current.localArtistId ? undefined : current.artistId!, limit: 25,
      }, controller.signal)
      if (controller.signal.aborted || disposed) return
      pool.push(...response.tracks)
    } catch {
      if (!controller.signal.aborted && !disposed) {
        useAutoplay.setState({ message: 'Similar songs aren’t available right now. Try again or choose another artist.' })
      }
    } finally {
      if (fetchAbort === controller) {
        fetchAbort = null
        useAutoplay.setState({ loading: false })
        schedule()
      }
    }
  }

  function prepare(suggestion: Suggestion) {
    const controller = new AbortController()
    prepareAbort = controller
    preparingKey = suggestion.key
    useAutoplay.setState((state) => ({
      suggestions: state.suggestions.map((item) => item.key === suggestion.key ? { ...item, preparing: true } : item),
    }))
    void prepareSuggestion(suggestion.track, controller.signal).then((ready) => {
      if (controller.signal.aborted || disposed) return
      failures = 0
      useAutoplay.setState((state) => ({
        message: null,
        suggestions: state.suggestions.map((item) => item.key === suggestion.key ? { ...item, ready, preparing: false } : item),
      }))
    }).catch(() => {
      if (controller.signal.aborted || disposed) return
      failures += 1
      rejectSuggestion(suggestion.key)
      useAutoplay.setState({ message: 'Couldn’t prepare that song. Trying another suggestion…' })
      if (failures >= 5) {
        stopWaiting()
        useAutoplay.setState({ message: 'Similar songs couldn’t be played. Try again later.' })
      }
    }).finally(() => {
      if (prepareAbort === controller) {
        prepareAbort = null
        preparingKey = null
      }
      schedule()
    })
  }

  function pump() {
    const player = usePlayer.getState()
    const current = player.current()
    if (!current || !useAutoplay.getState().enabled || player.preview) return

    if (waiting && (waiting.intent !== player.intentVersion || waiting.uid !== current.uid)) stopWaiting()
    if (waiting && !waiting.selected && player.index < player.queue.length - 1) {
      stopWaiting()
      player.playAt(player.index + 1)
      return
    }

    const blocked = new Set(excluded)
    player.queue.forEach((item) => {
      blocked.add(`id:${item.trackId}`)
      recordingKeys({ title: item.title, artist_name: item.artistName, version: item.version, explicit: item.explicit, isrc: item.isrc, recording_id: item.recordingId }).forEach(key => blocked.add(key))
    })
    const previous = useAutoplay.getState().suggestions
    const suggestions = previous.filter((item) => !resultKeys(item.track).some((key) => blocked.has(key)))
    suggestions.forEach((item) => resultKeys(item.track).forEach((key) => blocked.add(key)))
    while (suggestions.length < 5 && pool.length) {
      const track = pool.shift()!
      const keys = resultKeys(track)
      if (keys.some((key) => blocked.has(key))) continue
      keys.forEach((key) => blocked.add(key))
      suggestions.push({ key: `${track.provider}:${track.external_id}`, track, ready: null, preparing: false })
    }
    if (suggestions.length !== previous.length || suggestions.some((item, i) => item !== previous[i])) {
      useAutoplay.setState({ suggestions })
    }

    if (suggestions.length < 5 && !fetchAbort && lastFetch !== current.uid && (current.localArtistId || current.artistId)) {
      void fetchSuggestions(current)
    }
    if (!suggestions.length && !fetchAbort) {
      stopWaiting()
      if (!useAutoplay.getState().message) useAutoplay.setState({ message: 'No more similar songs found for this artist.' })
      return
    }
    if (failures >= 5) return

    const next = waiting?.selected ? suggestions.find((item) => item.key === waiting?.selected) : suggestions[0]
    if (!next) return
    if (waiting && next.ready) {
      resultKeys(next.track).forEach((key) => excluded.add(key))
      stopWaiting()
      useAutoplay.setState({ suggestions: suggestions.filter((item) => item.key !== next.key) })
      // Only consumed songs enter the real queue; manual additions always stay ahead.
      player.playSuggested(next.ready)
      return
    }
    const nearEnd = player.index === player.queue.length - 1 && player.repeat === 'off'
    if (!next.ready && !prepareAbort && (waiting || (player.isPlaying && nearEnd))) prepare(next)
  }

  const unregister = registerContinuation(() => {
    const player = usePlayer.getState()
    const current = player.current()
    if (!useAutoplay.getState().enabled || (!current?.localArtistId && !current?.artistId) || player.preview || failures >= 5) return false
    waiting = { intent: player.intentVersion, uid: current.uid }
    usePlayer.setState({ awaitingContinuation: true })
    schedule()
    return true
  })

  actions = {
    dismiss: rejectSuggestion,
    play: (key) => {
      const player = usePlayer.getState()
      const current = player.current()
      if (!current || !useAutoplay.getState().suggestions.some((item) => item.key === key)) return
      player.pause()
      failures = 0
      waiting = { intent: usePlayer.getState().intentVersion, uid: current.uid, selected: key }
      usePlayer.setState({ awaitingContinuation: true })
      schedule()
    },
    retry: () => { lastFetch = ''; failures = 0; useAutoplay.setState({ message: null }); schedule() },
  }

  function sync() {
    const player = usePlayer.getState()
    const current = player.current()
    if (player.session !== session) {
      session = player.session
      currentUid = current?.uid ?? ''
      excluded = new Set()
      reset()
    } else if (current?.uid !== currentUid) {
      currentUid = current?.uid ?? ''
      if (!current?.suggested) reset()
    }
    if (waiting && (waiting.intent !== player.intentVersion || (!waiting.selected && player.repeat !== 'off') || player.preview)) stopWaiting()
    schedule()
  }
  const unsubscribePlayer = usePlayer.subscribe((state, previous) => {
    if (state.queue !== previous.queue || state.index !== previous.index || state.session !== previous.session ||
        state.intentVersion !== previous.intentVersion || state.isPlaying !== previous.isPlaying ||
        state.repeat !== previous.repeat || state.preview !== previous.preview) sync()
  })
  const unsubscribePreference = useAutoplay.subscribe((state, previous) => {
    if (state.enabled !== previous.enabled) { reset(); schedule() }
  })
  const unsubscribePrivate = useIncognito.subscribe(() => { reset(); schedule() })
  sync()

  return () => {
    disposed = true
    unregister()
    unsubscribePlayer()
    unsubscribePreference()
    unsubscribePrivate()
    actions = null
    reset()
  }
}
